<template>
  <dp-container v-if="loading || !ready">
    <dp-loading />
  </dp-container>
  <!-- Desktop -->
  <dp-container
    v-else-if="isDesktop && cardReady && productLoaded && !walletCardError"
    :class="{ disabled: itemLoading || disableLoading }"
  >
    <ValidationObserver ref="walletcard_style_form" slim>
      <form class="mx-4 mt-4" @submit.prevent="addWalletCard">
        <ProductConfigBackLink product-type="WalletCard" />
        <!-- Screen 1 -->
        <section v-show="screen1">
          <h1 class="text-center">
            {{ $t('personalizedWalletCard.style.title', [petName]) }}
          </h1>
          <h2 v-if="productGroupStyle" class="text-center text-dark mx-3 px-lg-5 h3" v-html="productGroupStyle.description" />

          <b-row class="mt-lg-5">
            <!-- Preview Column -->
            <b-col sm="12" lg="5" xl="5">
              <section class="sticky">
                <dp-wallet-card
                  v-if="svgBaseReady"
                  ref="wallet_card_builder"
                  class="flex-column notranslate"
                  col-styling="col-lg-12"
                  translate="no"
                  :card="card"
                  :front-design="frontDesignSvgImagePath"
                  :back-design="backDesignSvgImagePath"
                  :front-title="frontAndBackDesignSvgTitle.front"
                  :back-title="frontAndBackDesignSvgTitle.back"
                  @designLoaded="designLoaded"
                  @error="walletCardError = true"
                />
              </section>
            </b-col>
            <!-- Configure Column -->
            <b-col sm="12" lg="6" offset-lg="1">
              <!-- Change Pet Link -->
              <section v-if="redirectToWalletCardStore" class="mb-2">
                <b-link :to="{ name: 'WalletCardProduct', params: { styleId: styleId, defaultPetId: petId } }">
                  <strong>{{ $t('personalizedWalletCard.style.links.changePet') }}</strong>
                </b-link>
              </section>
              <!-- Your Pet's Info -->
              <section v-if="petId">
                <h2 class="mb-0">{{ $t('personalizedWalletCard.style.section.petInfo.title') }}</h2>
                <p class="text-dark">
                  {{ $t('personalizedWalletCard.style.section.petInfo.description', [petName]) }}
                  <b-link
                    :href="editPetInfoLink(petId)"
                    v-text="$t('personalizedWalletCard.style.section.petInfo.link')"
                    class="text-decoration-underline"
                  />
                </p>
              </section>
              <!-- Pick Your Headline -->
              <section class="mb-4">
                <wallet-card-headline-select
                  ref="wallet_card_headline_select"
                  :options="headlineOptions"
                  @selectedHeadline="selectedHeadline"
                />
              </section>
              <!-- Choose How Your Pet's Name Appears -->
              <section class="mb-4">
                <ValidationObserver ref="pet_name_appears" v-slot="{ validated }" class="text-left" slim>
                  <wallet-card-text-field
                    ref="wallet_card_pet_name"
                    :character-limit="petNameCharacterLimit"
                    :title="$t('personalizedWalletCard.style.section.petName.title')"
                    :validated="validated"
                    :value="petName"
                    immediate-validation
                    name="pet-name"
                    @input="updatePetName"
                  />
                </ValidationObserver>
              </section>
              <!-- Choose How Your Address Appears -->
              <section class="mb-4">
                <ValidationObserver ref="pet_owner_address_appears" v-slot="{ validated }" class="text-left" slim>
                  <wallet-card-text-field
                    ref="wallet_card_pet_owner_address"
                    :character-limit="poAddressCharacterLimit"
                    :title="$t('personalizedWalletCard.style.section.poAddress.title')"
                    :validated="validated"
                    :value="poAddress"
                    immediate-validation
                    name="address"
                    @input="updatePetOwnerAddress"
                  />
                </ValidationObserver>
              </section>
              <!-- Pick Your Color -->
              <section v-if="colorOptions" class="mb-4">
                <wallet-card-color-select ref="wallet_card_color_select" :options="colorOptions" @selectedColor="selectedColor" />
              </section>
              <!-- Your Pet's Photo -->
              <section v-if="petImages" class="mb-4">
                <wallet-card-photo-select
                  ref="wallet_card_photo_select"
                  :images="petImages"
                  :pet-id="petId"
                  :loading="loadingPetPhoto"
                  :photo-placeholder="card.PET_IMAGE"
                  @selectedPhoto="selectedPhoto"
                />
              </section>
              <!-- Pet Temperament -->
              <section v-if="temperamentOptions" class="mb-2">
                <wallet-card-temperament-select
                  ref="wallet_card_temperament_select"
                  :options="temperamentOptions"
                  @selectedTemperaments="selectedTemperaments"
                />
              </section>
              <!-- Emergency Contact Information -->
              <section class="mb-5">
                <ValidationObserver ref="p_emergency_contact_form" v-slot="{ validated }" slim class="text-left">
                  <wallet-card-emergency-contact
                    ref="wallet_card_emergency_contact"
                    :validated="validated"
                    :character-limit="emergencyContactCharacterLimit"
                    :character-limit-optional="emergencyContactOptionalCharacterLimit"
                    @updateEmergencyName="updateEmergencyName"
                    @updateEmergencyPhone="updateEmergencyPhone"
                    @updateEmergencyNameOptional="updateEmergencyNameOptional"
                    @updateEmergencyPhoneOptional="updateEmergencyPhoneOptional"
                  />
                </ValidationObserver>
              </section>
              <!-- Next/Remove from Order CTA -->
              <b-row>
                <b-col offset-lg="6" lg="6">
                  <p class="mb-3">
                    <b-link @click="removeWalletCard">
                      <u>{{ $t('personalizedWalletCard.buttons.remove') }}</u>
                    </b-link>
                  </p>
                  <b-button id="wallet-card-next" variant="primary" block :disabled="isNextButtonDisabled" @click.prevent="validateCard">
                    {{ $t('personalizedWalletCard.buttons.next') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </section>

        <!-- Screen 2 -->
        <section v-show="screen2">
          <!-- Card Preview -->
          <wallet-card-preview
            ref="wallet_card_preview"
            :additional="additionalHtml"
            :back-label="card.backLabel"
            :back-svg="backSvg"
            :front-label="card.frontLabel"
            :front-svg="frontSvg"
            :front-title="frontAndBackDesignSvgTitle.front"
            :back-title="frontAndBackDesignSvgTitle.back"
            :item-loading="itemLoading || disableLoading"
            :pet-name="petName"
            :upload-designs-error="uploadDesignsError"
            @edit="edit"
            @remove="removeWalletCard"
          />
        </section>
      </form>
    </ValidationObserver>
  </dp-container>
  <!-- Mobile -->
  <dp-container
    v-else-if="isMobile && cardReady && productLoaded && !walletCardError"
    class="dp-container-wrapper"
    :class="{ disabled: itemLoading || disableLoading }"
  >
    <template v-slot:dp-container-header>
      <section v-show="currentStep !== totalSteps" class="mt-3 mx-4">
        <ProductConfigBackLink class="mb-3 d-block" product-type="WalletCard" />
        <dp-wallet-card
          v-if="svgBaseReady"
          ref="wallet_card_builder"
          class="d-flex notranslate"
          col-styling="col-lg-12"
          translate="no"
          :hide-front="currentStep === 4"
          :hide-back="currentStep !== 4"
          :card="card"
          :front-design="frontDesignSvgImagePath"
          :back-design="backDesignSvgImagePath"
          :front-title="frontAndBackDesignSvgTitle.front"
          :back-title="frontAndBackDesignSvgTitle.back"
          @designLoaded="designLoaded"
          @error="walletCardError = true"
        />
      </section>
    </template>
    <ValidationObserver ref="walletcard_style_form" slim>
      <form ref="p_style_form" class="mx-4">
        <section>
          <b-row>
            <b-col sm="12" class="text-left">
              <!-- Pick Your Headline / Step 1-->
              <section v-show="currentStep === 1" class="mb-5">
                <h1 class="text-center mt-2">
                  {{ $t('personalizedWalletCard.style.title', [petName]) }}
                </h1>
                <h3 v-if="productGroupStyle" class="text-center text-dark mx-3 mb-4" v-html="productGroupStyle.description" />
                <!-- Change Pet Link -->
                <section v-if="redirectToWalletCardStore" v-show="currentStep === 1" class="mb-2">
                  <b-link :to="{ name: 'WalletCardProduct', params: { styleId: styleId, defaultPetId: petId } }">
                    <strong>{{ $t('personalizedWalletCard.style.links.changePet') }}</strong>
                  </b-link>
                </section>
                <!-- Your Pet's Info -->
                <section v-if="petId" v-show="currentStep === 1">
                  <h2 class="mb-0">{{ $t('personalizedWalletCard.style.section.petInfo.title') }}</h2>
                  <p class="text-dark">
                    {{ $t('personalizedWalletCard.style.section.petInfo.description', [petName]) }}
                    <b-link
                      :href="editPetInfoLink(petId)"
                      v-text="$t('personalizedWalletCard.style.section.petInfo.link')"
                      class="text-decoration-underline"
                    />
                  </p>
                </section>
                <!-- Choose How Your Pet's Name Appears -->
                <section v-show="currentStep === 1" class="mb-4">
                  <ValidationObserver ref="pet_name_appears" v-slot="{ validated }" class="text-left" slim>
                    <wallet-card-text-field
                      ref="wallet_card_pet_name"
                      :character-limit="petNameCharacterLimit"
                      :title="$t('personalizedWalletCard.style.section.petName.title')"
                      :validated="validated"
                      :value="petName"
                      immediate-validation
                      name="pet-name"
                      @input="updatePetName"
                    />
                  </ValidationObserver>
                </section>
                <!-- Choose How Your Address Appears -->
                <section v-show="currentStep === 1" class="mb-4">
                  <ValidationObserver ref="pet_owner_address_appears" v-slot="{ validated }" class="text-left" slim>
                    <wallet-card-text-field
                      ref="wallet_card_pet_owner_address"
                      :character-limit="poAddressCharacterLimit"
                      :title="$t('personalizedWalletCard.style.section.poAddress.title')"
                      :validated="validated"
                      :value="poAddress"
                      immediate-validation
                      name="address"
                      @input="updatePetOwnerAddress"
                    />
                  </ValidationObserver>
                </section>
                <ValidationObserver ref="p_style_form_step_1" slim>
                  <wallet-card-headline-select
                    ref="wallet_card_headline_select"
                    :options="headlineOptions"
                    @selectedHeadline="selectedHeadline"
                  />
                </ValidationObserver>
              </section>
              <!-- Pick Your Color -->
              <section v-if="colorOptions" v-show="currentStep === 1" class="mb-5">
                <wallet-card-color-select ref="wallet_card_color_select" :options="colorOptions" @selectedColor="selectedColor" />
              </section>
              <!-- Your Pet's Photo / Step 2-->
              <section v-if="petImages" v-show="currentStep === 2" class="mb-4">
                <ValidationObserver ref="p_style_form_step_2" slim>
                  <wallet-card-photo-select
                    ref="wallet_card_photo_select"
                    :images="petImages"
                    :pet-id="petId"
                    :loading="loadingPetPhoto"
                    :photo-placeholder="card.PET_IMAGE"
                    @selectedPhoto="selectedPhoto"
                  />
                </ValidationObserver>
              </section>
              <!-- Pet Temperament / Step 3 -->
              <section v-if="temperamentOptions" v-show="currentStep === 3" class="mb-4">
                <ValidationObserver ref="p_style_form_step_3" slim>
                  <wallet-card-temperament-select
                    ref="wallet_card_temperament_select"
                    :options="temperamentOptions"
                    @selectedTemperaments="selectedTemperaments"
                  />
                </ValidationObserver>
              </section>
              <!-- Emergency Contact Information / Step 4 -->
              <section v-show="currentStep === 4" class="mb-4">
                <ValidationObserver ref="p_style_form_step_4" v-slot="{ validated }" slim>
                  <wallet-card-emergency-contact
                    ref="wallet_card_emergency_contact"
                    :validated="validated"
                    :character-limit="emergencyContactCharacterLimit"
                    :character-limit-optional="emergencyContactOptionalCharacterLimit"
                    @updateEmergencyName="updateEmergencyName"
                    @updateEmergencyPhone="updateEmergencyPhone"
                    @updateEmergencyNameOptional="updateEmergencyNameOptional"
                    @updateEmergencyPhoneOptional="updateEmergencyPhoneOptional"
                  />
                </ValidationObserver>
              </section>
              <!-- Card Preview / Step 5 -->
              <section v-show="currentStep === 5" class="mb-4">
                <!-- Card Preview -->
                <ValidationObserver ref="p_style_form_step_5" slim>
                  <wallet-card-preview
                    ref="wallet_card_preview"
                    :additional="additionalHtml"
                    :back-label="card.backLabel"
                    :back-svg="backSvg"
                    :front-label="card.frontLabel"
                    :front-svg="frontSvg"
                    :front-title="frontAndBackDesignSvgTitle.front"
                    :back-title="frontAndBackDesignSvgTitle.back"
                    :item-loading="itemLoading || disableLoading"
                    :pet-name="petName"
                    :upload-designs-error="uploadDesignsError"
                    @edit="edit"
                    @remove="removeWalletCard"
                  />
                </ValidationObserver>
              </section>
            </b-col>
          </b-row>
        </section>
        <Steps
          v-if="isWalletCard"
          class="fixed-bottom"
          :current-step="currentStep"
          :total-steps="totalSteps"
          :is-button-disabled="isNextButtonDisabled"
          @prevClick="prevClick"
          @nextClick="nextClick"
        >
          <template #prev-btn-content>
            <section>&lt; {{ $t('dp.generic.previous') }}</section>
          </template>
          <template #next-btn-content>
            <section v-if="currentStep !== totalSteps">{{ $t('personalizedWalletCard.buttons.next') }}</section>
            <section v-else>
              {{ $t('personalizedWalletCard.buttons.confirm') }}
              <div v-if="itemLoading || disableLoading" role="status" class="fa-pulse float-right mt-1">
                <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
                <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
              </div>
            </section>
          </template>
        </Steps>
      </form>
    </ValidationObserver>
  </dp-container>
  <!-- Not found / Error -->
  <product-not-found
    v-else
    col-class="pb-0"
    :not-found="!productLoaded"
    :error="walletCardError || !cardReady || !svgBaseReady"
    :back-to-store="true"
  />
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { DpContainer, DpGetImageUtil, DpLoading, DpWalletCard, DpSentry, DpFocusableElementMixins } from '@docupet/component-library';
import Steps from '@/components/library/Steps';
import WalletCardHeadlineSelect from '@/components/walletcards/WalletCardHeadlineSelect';
import WalletCardColorSelect from '@/components/walletcards/WalletCardColorSelect';
import WalletCardPhotoSelect from '@/components/walletcards/WalletCardPhotoSelect';
import WalletCardTemperamentSelect from '@/components/walletcards/WalletCardTemperamentSelect';
import WalletCardEmergencyContact from '@/components/walletcards/WalletCardEmergencyContact';
import WalletCardPreview from '@/components/walletcards/WalletCardPreview';
import { getBase64 } from '@/api/axios';
import { ProductStyleAnalyticsMixins } from '@/mixins/analytics/productStyle';
import ProductNotFound from '@/components/ProductNotFound';
import { ProductRedirectMixins } from '@/mixins/redirect';
import ProductConfigBackLink from '@/components/common/ProductConfigBackLink';
import baseLocaleUrl from '@/mixins/baseLocaleUrl';
import WalletCardTextField from '@/components/walletcards/WalletCardTextField';
import { NotificationMixins } from '@/mixins/notification';
import { LanguageCheckMixins } from '@/mixins/languageCheck';

export default {
  name: 'WalletCardStyle',
  components: {
    WalletCardTextField,
    ProductConfigBackLink,
    DpContainer,
    DpLoading,
    DpWalletCard,
    Steps,
    WalletCardHeadlineSelect,
    WalletCardColorSelect,
    WalletCardPhotoSelect,
    WalletCardTemperamentSelect,
    WalletCardEmergencyContact,
    WalletCardPreview,
    ProductNotFound,
  },
  mixins: [
    NotificationMixins,
    ProductStyleAnalyticsMixins,
    ProductRedirectMixins,
    baseLocaleUrl,
    LanguageCheckMixins,
    DpFocusableElementMixins,
  ],
  props: {
    styleId: {
      type: [String, Number],
      default: '',
      required: false,
    },
    petId: {
      type: [String, Number],
      default: '',
      required: false,
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      desktopWidth: 991,
      card: {
        primaryColor: '',
        secondaryColor: '',
        frontLabel: 'Front',
        backLabel: 'Back',
        HEADER: '',
        PET_IMAGE: '',
        fontFamily: 'Lato',
        petName: '',
        poAddress: '',
        temperaments: '',
      },
      ready: false,
      screen1: true,
      screen2: false,
      frontSvg: null,
      backSvg: null,
      currentStep: 1,
      additional: 0,
      disableLoading: false,
      uploadDesignsError: false,
      walletCardError: false,
      mappedPlaceholders: [],
      validateMappedPlaceholders: [
        'PET_NAME',
        'HEADER',
        'PET_BREED',
        'PET_SEX',
        'PET_DOB',
        'PET_COLOR',
        'PET_TEMPERAMENT',
        'PET_OWNER_DISPLAY_NAME',
        'PET_OWNER_ADDRESS',
        'PET_IMAGE',
        'EMERGENCY_CONTACT_ONE_DISPLAY_NAME',
        'EMERGENCY_CONTACT_ONE_PHONE_NUMBER',
        'EMERGENCY_CONTACT_TWO_DISPLAY_NAME',
        'EMERGENCY_CONTACT_TWO_PHONE_NUMBER',
        'DOCUPET_CONTACT_NUMBER',
        'PET_QR_CODE',
        'HOMESAFE_URL',
      ],
    };
  },
  metaTag() {
    return {
      ...this.$router.currentRoute.meta,
      title: `${this.selectedProduct?.name || ''} -`,
      meta: [
        { charset: 'utf-8' },
        {
          vmid: 'description',
          name: 'description',
          content: this.productGroupStyle?.description || '',
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.productGroupStyle?.description || '',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      loading: (state) => state.user.loading || state.order.loading,
      itemLoading: (state) => state.order.itemLoading,
      order: (state) => state.order.order,
      orderId: (state) => state.orderId,
      city: (state) => state.user.city,
      cityId: (state) => state.cityId,
      user: (state) => state.user.user,
      productGroups: (state) => state.walletCardProduct.groups,
      productStyle: (state) => state.walletCardProduct.style,
      loadingPetPhoto: (state) => state.walletCardProduct.loadingPetPhoto,
      selectedPet(state) {
        return state.order?.order?.petOwner?.pets?.find((pet) => pet.id === this.petId);
      },
    }),
    ...mapGetters({
      getRequiredPetByPetId: 'order/getRequiredPetByPetId',
    }),
    pet() {
      return this.requiredPet || this.selectedPet;
    },
    petName() {
      return this.pet?.name || '';
    },
    requiredPet() {
      return this.getRequiredPetByPetId(this.petId);
    },
    cardReady() {
      return Boolean(this.pet && this.user && this.productStyle && this.placeholders);
    },
    productLoaded() {
      return Boolean(this.productGroups?.length > 0 && this.productStyle?.products?.length > 0);
    },
    headlineOptions() {
      return [
        { value: null, text: 'Pet Identification' },
        { value: 0, text: 'Pet Identification' },
        { value: 1, text: 'Pet Information' },
      ];
    },
    colorOptions() {
      return this.productStyle?.products?.map((t) => t.primaryColor);
    },
    selectedProductId() {
      return this.productStyle?.products?.find((product) => product.primaryColor === this.card.primaryColor)?.id;
    },
    temperamentOptions() {
      return this.productStyle?.petTemperaments
        ?.map((t) => ({ value: t.name, text: t.displayName }))
        .sort((a, b) => a.value.localeCompare(b.value));
    },
    petImages() {
      return this.productStyle?.petPhotos ? this.productStyle.petPhotos.slice().reverse() : null;
    },
    productGroupStyle() {
      return this.getProductByType('productStyle');
    },
    productGroupStylePrice() {
      return this.getProductByType('productGroup')?.price;
    },
    totalSteps() {
      return this.isWalletCard && this.isMobile ? 5 : null;
    },
    isWalletCard() {
      return this.productStyle?.products?.length !== 0;
    },
    frontDesignSvgImagePath() {
      return this.selectedProduct?.frontDesign?.original ? this.DpGetImageUtil(this.selectedProduct?.frontDesign?.original) : null;
    },
    backDesignSvgImagePath() {
      return this.selectedProduct?.backDesign?.original ? this.DpGetImageUtil(this.selectedProduct?.backDesign?.original) : null;
    },
    svgBaseReady() {
      return Boolean(this.frontDesignSvgImagePath) && Boolean(this.backDesignSvgImagePath);
    },
    additionalHtml() {
      const productId = this.productStyle?.products?.find((product) => product.id === this.selectedProductId);

      // get ${0} placeholder
      const placeholder = new RegExp('\\$\\{[0-9]+}', 'g');

      return this.productStyle?.additionalCardsHtml?.replace(placeholder, this.$n(productId?.duplicateDiscountedAmount, 'currency'));
    },
    addItemRedirectTo() {
      if (this.requiredPet) {
        return { name: this.determineRedirectRoute || 'ContinueToLicensing' };
      }
      return { name: 'WalletCardStore' };
    },
    selectedProduct() {
      return this.productStyle?.products?.find((product) => product.id === this.selectedProductId);
    },
    labels() {
      return this.selectedProduct?.templateFields ? this.selectedProduct?.templateFields : null;
    },
    placeholders() {
      return this.productStyle?.placeholders ? this.productStyle?.placeholders : null;
    },
    petCheckoutWalletCardConfigurationUrl() {
      if (!this.order?.groups?.length) return null;
      
      const configurations = this.order.groups
        .map(group => group.walletCardConfigurations)
        .filter(Boolean);
      
      return configurations.length ? 
        configurations.find(config => config?.optedIn && config?.url)?.url : 
        null;
    },
    isMobile() {
      return this.windowWidth <= this.desktopWidth;
    },
    isDesktop() {
      return this.windowWidth > this.desktopWidth;
    },
    emergencyContactCharacterLimit() {
      return this.labels?.find((label) => label.valueType == 'EMERGENCY_CONTACT_ONE_DISPLAY_NAME')?.valueCharacterLimit;
    },
    emergencyContactOptionalCharacterLimit() {
      return this.labels?.find((label) => label.valueType == 'EMERGENCY_CONTACT_TWO_DISPLAY_NAME')?.valueCharacterLimit;
    },
    validatePlaceholders() {
      return this.mappedPlaceholders.every((placeholder) => {
        return this.validateMappedPlaceholders.includes(placeholder.valueType);
      });
    },
    redirectToWalletCardStore() {
      return this.determineRedirectRoute === 'WalletCardStore';
    },
    petNameCharacterLimit() {
      return this.labels?.find((label) => label.valueType == 'PET_NAME')?.valueCharacterLimit;
    },
    poAddressCharacterLimit() {
      return this.labels?.find((label) => label.valueType == 'PET_OWNER_ADDRESS')?.valueCharacterLimit;
    },
    poAddress() {
      return this.placeholders?.find((placeholder) => placeholder?.type?.valueType == 'PET_OWNER_ADDRESS')?.value;
    },
    isNextButtonDisabled() {
      return this.languageError || this.itemLoading;
    },
    frontAndBackDesignSvgTitle() {
      const svgDetails = this.placeholders?.reduce(
        (details, item) => {
          const valueType = item?.type?.valueType;
          if (Object.hasOwn(details, valueType)) {
            details[valueType] = item.value || '';
          }
          return details;
        },
        {
          PET_NAME: '',
          PET_BREED: '',
          PET_SEX: '',
          PET_DOB: '',
          PET_COLOR: '',
          PET_OWNER_DISPLAY_NAME: '',
          PET_OWNER_ADDRESS: '',
          DOCUPET_CONTACT_NUMBER: '',
          HOMESAFE_URL: '',
        }
      );
      return {
        front:
          this.$t('personalizedWalletCard.custom.front', {
            color: this.selectedProduct?.name || 'Light Blue',
            name: this.card.petName || svgDetails.PET_NAME,
            breed: svgDetails.PET_BREED,
            sex: svgDetails.PET_SEX,
            DoB: svgDetails.PET_DOB,
            coat: svgDetails.PET_COLOR,
            poName: svgDetails.PET_OWNER_DISPLAY_NAME,
            address: this.card.poAddress || svgDetails.PET_OWNER_ADDRESS,
            temperaments: this.card.temperaments,
          }) ?? '',
        back:
          this.$t('personalizedWalletCard.custom.back', {
            color: this.selectedProduct?.name || 'Light Blue',
            phone: svgDetails.DOCUPET_CONTACT_NUMBER,
            homeSafeUrl: svgDetails.HOMESAFE_URL,
          }) ?? '',
      };
    },
  },
  created() {
    window.addEventListener('resize', this.windowResize);
    (async () => {
      if (!this.productGroups || this.productGroups?.length === 0) {
        await this.$store.dispatch('walletCardProduct/fetchGroups', { cityId: this.city?.id || this.cityId });
        while (this.loading) await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    })().then(() => {
      let style;
      if (!this.productGroupStyle?.code && this.productGroups?.length !== 0) {
        style = this.productGroups?.find((group) => group.id == this.styleId)?.productStyles?.[0];
        this.$store.dispatch('walletCardProduct/setStyle', style);
      }
      this.$store
        .dispatch('walletCardProduct/fetchWalletCardCustomization', {
          petId: this.petId,
          productStyleId: style?.id || this.styleId,
        })
        .then(() => (this.ready = true))
        .catch(() =>
          this.$router.push({
            name: this.determineRedirectRoute || 'WalletCardStore',
          })
        );
    });
  },
  mounted() {
    this.$root?.$children?.[0]?.$refs?.['footer']?.$el?.setAttribute('hidden', '');
  },
  destroyed() {
    this.$root?.$children?.[0]?.$refs?.['footer']?.$el?.setAttribute('hidden', '');
    this.$store.dispatch('walletCardProduct/removeStyle', { vm: this });
    window.removeEventListener('resize', this.windowResize);
  },
  methods: {
    getBase64,
    DpGetImageUtil,
    edit() {
      this.screen2 = false;
      this.screen1 = true;
      this.uploadDesignsError = false;
    },
    getProductByType(product) {
      for (let p of this.productGroups) {
        for (let s of p.productStyles) {
          if (s.id === this.styleId) {
            return product === 'productGroup' ? p : s;
          }
        }
      }
      return null;
    },
    validateBeforeAddCard() {
      this.validateCard().then((response) => {
        if (response) {
          this.addWalletCard();
        }
      });
    },
    async validateCard() {
      const ref = this.$refs.walletcard_style_form;
      const isValid = await ref?.validate();
      if (!isValid) {
        this.scrollToError(ref);
        return;
      }
      if (this.isDesktop) {
        this.screen2 = true;
        this.screen1 = false;
        this.setWalletCardPreview();

        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }
      return true;
    },
    setWalletCardPreview() {
      this.frontSvg = this.$refs.wallet_card_builder?.exportCards().front;
      this.backSvg = this.$refs.wallet_card_builder?.exportCards().back;
    },
    async checkWalletCardConfiguration() {
      await this.$store.dispatch('order/fetchOrder', { vm: this, orderId: this.orderId });
      if (this.petCheckoutWalletCardConfigurationUrl) {
        // Go to next Customize Wallet Card if available
        const redirectPath = this.$router?.currentRoute?.query?.['redirect_path'];
        window.location = `${this.petCheckoutWalletCardConfigurationUrl}?redirect_path=${redirectPath}`;
      } else if (this.addItemRedirectTo) {
        if (this.redirectToWalletCardStore) {
          this.$emit('showSideBasket');
        }
        await this.$router.push(this.addItemRedirectTo);
      }
    },
    async addWalletCard() {
      if (!this.selectedProductId) return;
      if (this.user && this.user.id) {
        // Add child for every additional card
        this.additional = this.$refs.wallet_card_preview?.counter;
        const childrenCount = Array.from({ length: this.additional }, () => ({ productId: this.selectedProductId }));
        this.analyticsTrackAddItem();

        const details = {
          productId: this.selectedProductId,
          petId: this.petId,
          children: childrenCount.length > 0 ? childrenCount : null,
        };

        this.disableLoading = true;
        this.uploadDesignsError = false;
        await this.$store.dispatch('order/addItem', {
          vm: this,
          orderId: this.order?.id || this.orderId,
          productDetails: details,
        });

        // Find the wallet card order item from the response
        const orderItem = this.order?.items.find(
          (item) =>
            item.product?.type === 'WALLET_CARD' &&
            item.group?.pet?.id === this.petId &&
            item.product?.id === this.selectedProductId &&
            !item.product?.image?.original &&
            item.children?.length === childrenCount.length
        );

        if (!orderItem) {
          return;
        }

        try {
          await this.beforeUploadValidateWalletCardDesignSvg();
          const base64FrontData = `data:image/svg+xml;base64,${window.btoa(this.frontSvg)}`;
          const base64BackData = `data:image/svg+xml;base64,${window.btoa(this.backSvg)}`;

          this.$store
            .dispatch('order/uploadWalletCardDesigns', {
              orderItemId: orderItem.id,
              frontDesign: base64FrontData,
              backDesign: base64BackData,
            })
            .then((response) => {
              if ((response.status === 201 || response.statusText === 'Created') && this.addItemRedirectTo) {
                this.checkWalletCardConfiguration();
              }
            })
            .catch(async (error) => await this.handleWalletCardDesignUploadError(error, orderItem));
        } catch (e) {
          await this.handleWalletCardDesignUploadError(e, orderItem);
        }
      }
    },
    beforeUploadValidateWalletCardDesignSvg() {
      if (!this.frontSvg || !this.backSvg) {
        throw new Error('Invalid SVG');
      }

      [this.frontSvg, this.backSvg].map((svgContent) => {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');

        const parseErrors = svgDoc.getElementsByTagName('parsererror');

        if (parseErrors.length > 0) {
          throw new Error('Invalid SVG');
        }

        this.mappedPlaceholders.forEach((object) => {
          if (object.valueType && object.labelName) {
            const valueType = object.valueType;
            const labelId = valueType + '_LABEL';

            const labelElement = svgDoc.getElementById(labelId);
            const normalizedLabelName = object.labelName.replaceAll('\n', '');

            if (labelElement?.textContent && labelElement.textContent !== normalizedLabelName) {
              throw new Error('Translations are not allowed');
            }
          }
        });
      });
    },
    async handleWalletCardDesignUploadError(error = null, walletCardOrderItem = null) {
      this.disableLoading = false;
      this.uploadDesignsError = true;

      if (walletCardOrderItem) {
        await this.$store.dispatch('order/removeItem', {
          orderId: this.order?.id || this.orderId,
          itemId: walletCardOrderItem.id,
        });
      }

      if (error) {
        DpSentry.withScope((scope) => {
          scope.setExtras({
            frontSvgContent: this.frontSvg,
            backSvgContent: this.backSvg,
          });
          DpSentry.captureException(error);
        });
        console.error(error);
      }
    },
    async removeWalletCard() {
      this.disableLoading = true;
      try {
        if (this.requiredPet) {
          await this.$store.dispatch('order/removeWalletCard', {
            orderId: this.order?.id || this.orderId,
            petId: this.petId,
            optInForWalletCard: false,
          });
        }

        await this.checkWalletCardConfiguration();
      } catch (e) {
        this.disableLoading = false;
        throw e;
      }
    },
    analyticsTrackAddItem() {
      if (window?.dataLayer && this.selectedProduct) {
        window?.dataLayer?.push({
          ecommerce: null,
        });
        const items = [];
        const _itemData = {
          quantity: 1 + this.additional,
          currency: `${this.order?.city?.country || 'US'}D`,
          item_list_id: this.productStyle?.id || '',
          item_list_name: this.productGroupStyle?.code?.toUpperCase() || '',
          price: Number(this.productGroupStylePrice) + Number(this.selectedProduct.duplicateDiscountedAmount * this.additional),
        };
        items.push({
          ..._itemData,
          item_id: this.selectedProduct?.id || '',
          item_name: this.selectedProduct?.name?.toUpperCase() || '',
          item_category: this.selectedProduct?.type?.toUpperCase() || '',
        });
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            items: items,
            currency: `${this.order?.city?.country || 'US'}D`,
            value: Number(items?.reduce((p, c) => p + Number(c.price), 0)).toFixed(2),
          },
        });
      }
    },
    selectedHeadline(headline) {
      this.card.HEADER = headline.toUpperCase();

      this.$refs.wallet_card_builder?.fillElementById('HEADER', this.card.HEADER);
    },
    selectedColor(color) {
      this.card.primaryColor = color;
      this.card.secondaryColor = this.selectedProduct?.secondaryColor;

      this.$refs.wallet_card_builder?.fillColorElements(this.card.primaryColor, this.card.secondaryColor);
    },
    selectedPhoto(photo) {
      this.card.PET_IMAGE = photo;

      this.$refs.wallet_card_builder?.fillElementById('PET_IMAGE', this.card.PET_IMAGE);
    },
    selectedTemperaments(temperaments) {
      const formattedTemperaments = temperaments.join(', ');

      this.card.temperaments = formattedTemperaments;
      this.$refs.wallet_card_builder?.fillElementById('PET_TEMPERAMENT', formattedTemperaments);
    },
    updateEmergencyName(name) {
      this.$refs.wallet_card_builder?.fillElementById('EMERGENCY_CONTACT_ONE_DISPLAY_NAME', name);
    },
    updateEmergencyPhone(phone) {
      this.$refs.wallet_card_builder?.fillElementById('EMERGENCY_CONTACT_ONE_PHONE_NUMBER', phone);
    },
    updateEmergencyNameOptional(name) {
      this.$refs.wallet_card_builder?.fillElementById('EMERGENCY_CONTACT_TWO_DISPLAY_NAME', name);
    },
    updateEmergencyPhoneOptional(phone) {
      this.$refs.wallet_card_builder?.fillElementById('EMERGENCY_CONTACT_TWO_PHONE_NUMBER', phone);
    },
    updatePetName(petName) {
      this.card.petName = petName;
      this.$refs.wallet_card_builder?.fillElementById('PET_NAME', petName);
    },
    updatePetOwnerAddress(poAddress) {
      this.card.poAddress = poAddress;
      this.$refs.wallet_card_builder?.fillElementById('PET_OWNER_ADDRESS', poAddress);
    },
    windowResize() {
      this.windowWidth = window.innerWidth;
      this.$root?.$children?.[0]?.$refs?.['footer']?.$el?.setAttribute('hidden', '');
    },
    nextClick() {
      if (this.currentStep === 4) {
        this.setWalletCardPreview();
      }
      this.$refs?.[`p_style_form_step_${this.currentStep}`]?.validate().then(async (success) => {
        if (!success) {
          this.scrollToError(this.$refs?.[`p_style_form_step_${this.currentStep}`]);
        } else this.currentStep < this.totalSteps ? this.currentStep++ : this.validateBeforeAddCard();
      });
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    },
    prevClick() {
      if (this.currentStep > 1) this.currentStep--;
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    },
    async designLoaded(ready) {
      if (this.placeholders && this.labels && ready) {
        this.mappedPlaceholders = [];
        for (let i = 0; i < this.labels.length; i++) {
          // Check if QR code is not empty
          if (this.placeholders[i]?.type?.valueType === 'PET_QR_CODE' && !this.placeholders[i].value) {
            this.walletCardError = true;
            return;
          }
          // Convert pet image to base64
          if (this.placeholders[i]?.type?.valueType === 'PET_IMAGE') {
            const imageUrl = this.placeholders[i].value;
            this.card.PET_IMAGE = this.placeholders[i].value = (await this.getBase64(imageUrl).catch(() => {})) || null;
          }
          if (this.labels[i].valueType === 'HEADER') {
            this.labels[i].value = this.card.HEADER;
          }

          // In case backend returns double \
          if (this.labels[i].labelName) {
            this.labels[i].labelName = this.labels[i].labelName.replaceAll('\\n', '\n');
          }

          this.mappedPlaceholders.push({
            ...this.labels[i],
            ...this.placeholders?.find((placeholder) => placeholder?.type?.valueType === this.labels[i]?.valueType),
          });
        }
        if (!this.validatePlaceholders) {
          this.walletCardError = true;
          return;
        }
        await this.$refs.wallet_card_builder?.fillPlaceholders(this.mappedPlaceholders);
      }
    },
    editPetInfoLink(petId) {
      return this.$store.getters.generateFullUrl(`/account/full/account-app/edit-details/${petId}`);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.sticky {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  margin-top: 0;
  padding-top: 0;
  z-index: 999;
}
.disabled {
  pointer-events: none;
}
</style>
